import React, { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, Col, Form, Input, Row, message } from 'antd'
import { Store } from 'antd/lib/form/interface'
import { useSession } from '@slerp/accounts'
import { MerchantSettings } from '@slerp/controls'
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons'
import { createCategory } from '../../Actions'
import {
  GET_MERCHANT_SETTINGS,
  UPDATE_MERCHANT_ARRANGEMENT_SETTINGS,
  CREATE_CATEGORY
} from '../../ProductQueries'
import '../../Products.css'

const { useForm } = Form

interface CategoryFormProps {
  merchantCategories: Category[]
  onCancel?: () => void
  onStartSave?: () => void
  onEndSave?: () => void
}

interface Category {
  id: string
  name: string
  slug: string
}

const CategoryForm = (props: CategoryFormProps) => {
  const { onCancel, onStartSave, onEndSave } = props
  const [form] = useForm()
  const { merchant } = useSession()
  const [merchantSettings, setMerchantSettings] = useState<MerchantSettings>()
  const { data: merchantSettingsData } = useQuery(GET_MERCHANT_SETTINGS, {
    variables: { merchantSlug: merchant.slug }
  })
  const [updateMerchantArrangementSettings] = useMutation(
    UPDATE_MERCHANT_ARRANGEMENT_SETTINGS
  )
  const [createMerchantCategory, { error: createCategoryError }] =
    useMutation(CREATE_CATEGORY)

  useEffect(() => {
    if (merchantSettingsData) {
      const { setting } = merchantSettingsData.merchants[0]
      setMerchantSettings(setting)
    }
  }, [merchantSettingsData])

  const initialValues = {
    name: ''
  }

  const onFinish = (values: Store) => {
    const { name } = values

    if (merchantSettings) {
      form.validateFields().then(() => {
        onStartSave && onStartSave()
        return createCategory(
          merchant.slug,
          name,
          merchantSettings,
          updateMerchantArrangementSettings,
          createMerchantCategory,
          () =>
            message.error(
              `Unable to create category due to: ${createCategoryError?.message}`,
              5
            )
        )
          .then((result) => {
            onEndSave && onEndSave()
          })
          .catch((error) => {
            message.error(
              `Unable to create category due to: ${error.message}`,
              5
            )
          })
      })
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      data-testid='category-form'
    >
      <Row align='middle' gutter={[8, 0]}>
        <Col span={17}>
          <Form.Item name='name' className='mb-0'>
            <Input
              required
              placeholder='Name'
              data-testid='category-form-name'
            />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Button
            shape='circle'
            type='primary'
            icon={<SaveOutlined />}
            size='small'
            htmlType='submit'
            data-testid='category-form-add-category'
          />
        </Col>
        <Col span={3}>
          <Button
            shape='circle'
            icon={<DeleteOutlined />}
            size='small'
            data-testid='category-form-cancel-category'
            onClick={() => onCancel && onCancel()}
          />
        </Col>
      </Row>
    </Form>
  )
}

export default CategoryForm
